/**
 * ShareImageCard Component - Web Version
 * 
 * Branded card component that renders selected text with article metadata
 * for image capture. Based on the mobile app's ShareImageCard with web adaptations.
 * 
 * FEATURES:
 * - Selected text prominently displayed with quote styling
 * - Article title and URL metadata (toggleable)
 * - App branding with logo and name
 * - Light/dark theme support
 * - Optimized for html2canvas capture
 * - Responsive text sizing based on content length
 * - Social media friendly aspect ratio
 */

import React, { forwardRef, useMemo } from 'react';
import { parseMarkdownForShare } from '../utils/markdown';

// App configuration
const APP_CONFIG = {
  NAME: 'Read It Never',
  SLOGAN: 'Reading is slow. Just vibe it.',
};

// Font configuration optimized for html2canvas
const FONT_FAMILY = 'Arial, "Helvetica Neue", Helvetica, sans-serif';
const MONO_FONT_FAMILY = 'monospace';

// Utility function to truncate URL for display (more aggressive for image export)
function truncateUrl(url, maxLength = 35) {
  if (url.length <= maxLength) return url;
  
  // Try to preserve domain by removing protocol and www
  let displayUrl = url.replace(/^https?:\/\/(www\.)?/, '');
  
  if (displayUrl.length <= maxLength) return displayUrl;
  
  // Truncate with ellipsis
  return displayUrl.substring(0, maxLength - 3) + '...';
}


// Calculate dynamic font size based on text length (optimized for html2canvas)
function calculateTextSize(text) {
  const length = text.length;
  
  if (length < 50) {
    return { fontSize: '26px', lineHeight: '36px' };
  } else if (length < 100) {
    return { fontSize: '22px', lineHeight: '32px' };
  } else if (length < 200) {
    return { fontSize: '19px', lineHeight: '28px' };
  } else {
    return { fontSize: '17px', lineHeight: '26px' };
  }
}

const ShareImageCard = forwardRef(({ config, isDark = false }, ref) => {
  const textStyle = calculateTextSize(config.selectedText || '');
  
  // Determine if we should use markdown rendering
  const hasMd = !!config.selectedMarkdown && config.selectedMarkdown !== config.selectedText;
  const shouldUseMarkdown = (config.preserveFormatting || hasMd) && !!config.selectedMarkdown;
  const contentToRender = shouldUseMarkdown ? config.selectedMarkdown : config.selectedText;
  
  
  
  // Theme colors
  const colors = useMemo(() => {
    if (isDark) {
      return {
        background: '#1f2937', // gray-800
        text: '#f9fafb', // gray-50
        textSecondary: '#d1d5db', // gray-300
        textTertiary: '#9ca3af', // gray-400
        primary: '#3b82f6', // blue-500
        border: '#374151', // gray-700
        cardSecondary: '#374151', // gray-700
      };
    } else {
      return {
        background: '#ffffff', // white
        text: '#111827', // gray-900
        textSecondary: '#6b7280', // gray-500
        textTertiary: '#9ca3af', // gray-400
        primary: '#3b82f6', // blue-500
        border: '#e5e7eb', // gray-200
        cardSecondary: '#f3f4f6', // gray-100
      };
    }
  }, [isDark]);
  
  // Style overrides for the parsed markdown elements
  const markdownOverrides = useMemo(() => ({
    fontSize: textStyle.fontSize,
    lineHeight: textStyle.lineHeight,
    fontFamily: FONT_FAMILY,
    fontWeight: '400',
    color: colors.text,
  }), [colors, textStyle]);
  
  return (
    <div
      ref={ref}
      style={{
        backgroundColor: colors.background,
        width: '416px', // Fixed width for consistent image generation
        padding: '8px',
        boxSizing: 'border-box',
        WebkitFontSmoothing: 'antialiased',
        MozOsxFontSmoothing: 'grayscale',
        textRendering: 'optimizeLegibility',
      }}
    >
      <div style={{
        backgroundColor: colors.background,
        borderColor: colors.border,
        borderWidth: '1px',
        borderStyle: 'solid',
        borderRadius: '12px',
        padding: '20px',
        minHeight: '300px',
        overflow: 'hidden',
        boxSizing: 'border-box',
      }}>
        {/* Selected Text Section - Primary Content */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          paddingTop: '12px',
          paddingBottom: '12px',
          minHeight: '120px',
          flex: 1,
        }}>
          {/* Quote Mark */}
          <div style={{
            fontSize: '48px',
            fontWeight: 'bold',
            lineHeight: '48px',
            opacity: 0.3,
            color: colors.primary,
          }}>
            &quot;
          </div>
          
          {/* Selected Text - Conditional Rendering */}
          <div style={{
            marginTop: '8px',
            marginBottom: '8px',
            paddingLeft: '8px',
            paddingRight: '8px',
            flex: 1,
            width: '100%',
            textAlign: 'left',
          }}>
            {shouldUseMarkdown ? (
              <div style={{
                ...markdownOverrides,
                WebkitFontSmoothing: 'antialiased',
                MozOsxFontSmoothing: 'grayscale',
              }}>
                {parseMarkdownForShare(contentToRender)}
              </div>
            ) : (
              <div style={{
                color: colors.text,
                fontSize: textStyle.fontSize,
                lineHeight: textStyle.lineHeight,
                fontWeight: '400',
                fontFamily: FONT_FAMILY,
                whiteSpace: 'pre-wrap',
                WebkitFontSmoothing: 'antialiased',
                MozOsxFontSmoothing: 'grayscale',
              }}>
                {contentToRender}
              </div>
            )}
          </div>
          
          {/* Closing Quote Mark */}
          <div style={{
            fontSize: '48px',
            fontWeight: 'bold',
            lineHeight: '48px',
            opacity: 0.3,
            color: colors.primary,
          }}>
            &quot;
          </div>
        </div>

        {/* Divider */}
        <div style={{
          height: '1px',
          backgroundColor: colors.border,
          marginTop: '16px',
          marginBottom: '16px',
          opacity: 0.3,
        }} />

        {/* Article Metadata Section */}
        <div style={{ marginBottom: '20px', minHeight: '80px' }}>
          {config.includeTitle && config.articleTitle && (
            <div style={{
              fontSize: '14px',
              fontWeight: '600',
              lineHeight: '20px',
              marginBottom: '8px',
              textAlign: 'center',
              color: colors.text,
              fontFamily: FONT_FAMILY,
              minHeight: '44px', // Ensure space for 2 lines
              maxHeight: '44px', // 2 lines * 22px line height (~20px font)
              overflow: 'hidden',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              wordBreak: 'break-word',
              paddingLeft: '12px',
              paddingRight: '12px',
              WebkitFontSmoothing: 'antialiased',
            }}>
              {config.articleTitle}
            </div>
          )}
          
          {config.includeUrl && (
            <div style={{
              fontSize: '11px',
              lineHeight: '16px',
              textAlign: 'center',
              color: colors.textSecondary,
              fontFamily: MONO_FONT_FAMILY,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              marginTop: '2px',
              paddingLeft: '24px',
              paddingRight: '24px',
              minHeight: '16px', // Tighten URL row height
              paddingBottom: '2px', // Prevent cutoff with compact spacing
            }}>
              {truncateUrl(config.articleUrl)}
            </div>
          )}
        </div>

        {/* Branding Section */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          paddingTop: '8px',
          paddingBottom: '8px',
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '6px',
            marginBottom: '4px',
            height: '24px',
          }}>
            {/* App Logo */}
            <img
              src="/logo.png"
              alt="Read It Never Logo"
              crossOrigin="anonymous"
              style={{
                width: '24px',
                height: '24px',
                display: 'block',
                objectFit: 'contain',
                transform: 'none',
              }}
            />

            {/* App Name */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              height: '24px',
              fontSize: '14px',
              fontWeight: '600',
              letterSpacing: '0.5px',
              color: colors.textSecondary,
              fontFamily: FONT_FAMILY,
            }}>
              {APP_CONFIG.NAME}
            </div>
          </div>
          
          {/* Optional watermark */}
          <div style={{
            fontSize: '11px',
            fontStyle: 'italic',
            opacity: 0.6,
            color: colors.textTertiary,
            fontFamily: FONT_FAMILY,
          }}>
            {APP_CONFIG.SLOGAN}
          </div>
        </div>
      </div>
    </div>
  );
});

ShareImageCard.displayName = 'ShareImageCard';

export default ShareImageCard;